import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Checkbox, Typography, Box, Button as MuiButton,
  CircularProgress, FormControl, InputLabel, Select, MenuItem, IconButton,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { useGetIdentity, useGetOne, useNotify } from 'react-admin';
import { getToken } from '../authentication/authProvider';
import { EmailHtmlEditor } from '../leads/EmailHtmlEditor';
import { useFuneralClients } from '../funerals/hooks/useFuneralClients';
import { Client, Funeral } from '../types';
import { buildPartnerRefSubject } from './dispatch/notificationTemplates';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

/** Identifies who/what the email is about, for comms-log attribution and
 *  document lookup. Pass whichever ids are known at the call site. */
export interface EmailComposeContext {
  funeralId?: number;
  leadId?: string;
  clientId?: number;
}

/**
 * Who a CASE email goes to (context.funeralId set). The server resolves the
 * addresses and enforces the rules (plan §3.9): a client plus, optionally, the
 * case's other clients; or one partner and nobody else. Never free text.
 * Omitted on a case send → the client in context.clientId.
 */
export type CaseEmailRecipient =
  | { kind: 'client'; clientId: number }
  | { kind: 'partner'; partnerId: string; name: string; email: string };

interface EmailComposeDialogProps {
  open: boolean;
  onClose: () => void;
  recipient: { name?: string; email?: string | null };
  context: EmailComposeContext;
  /** Comma-separated default CC. Non-case sends only. */
  ccDefault?: string;
  caseRecipient?: CaseEmailRecipient;
  /** Plain text to start the message with (e.g. what was typed in the feed composer). */
  initialText?: string;
  /** Subject to start with (e.g. "Re: …" from the thread modal). */
  initialSubject?: string;
  /** Case sends: other case clients ticked in "Also send to" on open (reply all). */
  initialAlsoTo?: number[];
  /** The communications row this replies to: threads it on the family's side. */
  inReplyToCommunicationId?: string;
  onSent?: (sent: { subject: string; html: string }) => void;
}

/**
 * The API takes JSON bodies up to 20 MB, and uploads travel as base64 (+33%).
 * Documents picked from the case are fetched server-side and don't count.
 */
const MAX_UPLOAD_BYTES = 14 * 1024 * 1024;

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Typed plain text → the editor's HTML: one <p> per paragraph, <br> per line. */
const textToHtml = (text: string) =>
  text
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('');

const base64Bytes = (b64: string) => Math.floor((b64.length * 3) / 4);

interface DocAttachment {
  label: string;
  url: string;
  checked: boolean;
}

interface FileAttachment {
  filename: string;
  contentBase64: string;
}

interface TemplateOption {
  key: string;
  label: string;
}

export function EmailComposeDialog({
  open,
  onClose,
  recipient,
  context,
  ccDefault,
  caseRecipient,
  initialText,
  initialSubject,
  initialAlsoTo,
  inReplyToCommunicationId,
  onSent,
}: EmailComposeDialogProps) {
  const notify = useNotify();
  const { identity } = useGetIdentity();

  // A case send names its recipient; the addresses come from the database.
  const isCaseSend = context.funeralId != null;
  const partnerRecipient = caseRecipient?.kind === 'partner' ? caseRecipient : null;
  const primaryClientId = isCaseSend && !partnerRecipient
    ? (caseRecipient?.kind === 'client' ? caseRecipient.clientId : context.clientId)
    : undefined;
  const { data: caseFuneral } = useGetOne<Funeral>(
    'funerals',
    { id: context.funeralId ?? 0 },
    { enabled: open && isCaseSend },
  );
  // A partner email starts with "Ref: <deceased> <case #>" so the partner can
  // file it. Client emails and anything already typed are left alone.
  const { data: deceased } = useGetOne<Client>(
    'clients',
    { id: caseFuneral?.deceased_client_id ?? 0 },
    { enabled: open && !!partnerRecipient && !!caseFuneral?.deceased_client_id },
  );
  const partnerRefSubject =
    partnerRecipient && context.funeralId != null && deceased?.name
      ? buildPartnerRefSubject(deceased.name, context.funeralId)
      : '';
  const { coloredClients: caseClients } = useFuneralClients(
    open && isCaseSend && !partnerRecipient ? caseFuneral : undefined,
  );
  const primaryClient = caseClients.find((c) => c.id === primaryClientId);
  const otherClients = caseClients.filter((c) => c.id !== primaryClientId);
  const [alsoTo, setAlsoTo] = useState<number[]>([]);

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [docs, setDocs] = useState<DocAttachment[]>([]);
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [usedTemplate, setUsedTemplate] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [sending, setSending] = useState(false);

  // Reset + prefill on open.
  useEffect(() => {
    if (!open) return;
    setTo((recipient.email || '').trim());
    setCc(ccDefault || '');
    setBcc('');
    setAlsoTo(initialAlsoTo ?? []);
    setSubject(initialSubject ?? '');
    setHtml(initialText?.trim() ? textToHtml(initialText) : '');
    setFiles([]);
    setSelectedTemplate('');
    setUsedTemplate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Declared after the reset above so it runs after it on open. Fills only an
  // empty subject: a template or anything typed is never replaced.
  useEffect(() => {
    if (open && partnerRefSubject) setSubject((prev) => prev || partnerRefSubject);
  }, [open, partnerRefSubject]);

  // Load available templates once when the dialog opens.
  useEffect(() => {
    if (!open) return;
    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${baseUrl}/email/templates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setTemplates(await res.json());
      } catch {
        /* templates are optional */
      }
    })();
  }, [open]);

  // Load attachable documents for funerals.
  useEffect(() => {
    if (!open || !context.funeralId) {
      setDocs([]);
      return;
    }
    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${baseUrl}/documents/funeral/${context.funeralId}/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const next: DocAttachment[] = [];
        (data.funeral_documents || []).forEach((d: any) => {
          next.push({
            label: d.original_filename || d.document_type,
            url: `${baseUrl}/documents/funeral-document/${d.id}/download`,
            checked: false,
          });
        });
        (data.verified_documents || [])
          .filter((d: any) => d.status === 'valid')
          .forEach((d: any) => {
            next.push({
              label: `${d.document_type} (${d.verification_id})`,
              url: `${baseUrl}/documents/${d.id}/download`,
              checked: false,
            });
          });
        setDocs(next);
      } catch {
        /* documents optional */
      }
    })();
  }, [open, context.funeralId]);

  const applyTemplate = async (key: string) => {
    setSelectedTemplate(key);
    if (!key) return;
    setLoadingTemplate(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      const firstName = (recipient.name || '').trim().split(/\s+/)[0] || '';
      if (firstName) params.set('clientName', firstName);
      if (identity?.fullName) params.set('senderName', identity.fullName);
      const res = await fetch(
        `${baseUrl}/email/templates/${encodeURIComponent(key)}/preview?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Failed to load template');
      const data = await res.json();
      setSubject(data.subject || '');
      setHtml(data.html || '');
      setUsedTemplate(true);
    } catch (e: any) {
      notify(e.message || 'Failed to load template', { type: 'warning' });
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleAddFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    Array.from(fileList).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        setFiles((prev) => [...prev, { filename: file.name, contentBase64: base64 }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const uploadBytes = files.reduce((sum, f) => sum + base64Bytes(f.contentBase64), 0);
  const uploadsTooLarge = uploadBytes > MAX_UPLOAD_BYTES;
  const caseRecipientReady = partnerRecipient
    ? !!partnerRecipient.email
    : primaryClientId != null && (!primaryClient || !!primaryClient.email);
  const canSend = (isCaseSend ? caseRecipientReady : !!to.trim()) && !uploadsTooLarge;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const token = await getToken();
      const payload: any = {
        ...(isCaseSend ? {} : { to: to }),
        subject,
        html,
        attachmentUrls: docs.filter((d) => d.checked).map((d) => ({ url: d.url, filename: `${d.label}.pdf` })),
        attachmentsInline: files,
        subscriptionTracking: usedTemplate,
        ...(inReplyToCommunicationId ? { inReplyToCommunicationId } : {}),
        ...context,
      };
      if (isCaseSend) {
        payload.recipient = partnerRecipient
          ? { kind: 'partner', partnerId: partnerRecipient.partnerId }
          : { kind: 'client', clientIds: [primaryClientId, ...alsoTo] };
        // A partner send is filed on the case, never on a person.
        if (partnerRecipient) delete payload.clientId;
      } else {
        if (cc.trim()) payload.cc = cc;
        if (bcc.trim()) payload.bcc = bcc;
      }

      const res = await fetch(`${baseUrl}/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to send email');
      }
      notify('Email sent', { type: 'success' });
      onClose();
      onSent?.({ subject, html });
    } catch (e: any) {
      notify(e.message || 'Failed to send email', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Email{recipient.name ? ` to ${recipient.name}` : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {isCaseSend ? (
            <Box>
              {/* Case email: To comes from the chip the user picked. No free text,
                  no CC/BCC (plan §3.9) — add a person to the case to write to them. */}
              <TextField
                label="To"
                value={
                  partnerRecipient
                    ? `${partnerRecipient.name} <${partnerRecipient.email}>`
                    : primaryClient
                      ? `${primaryClient.name} <${primaryClient.email || 'no email on file'}>`
                      : recipient.name
                        ? `${recipient.name} <${recipient.email || ''}>`
                        : (recipient.email || '')
                }
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
              {!partnerRecipient && otherClients.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  {otherClients.map((c) => (
                    <Box key={c.id} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        size="small"
                        disabled={!c.email}
                        checked={alsoTo.includes(c.id)}
                        onChange={(e) =>
                          setAlsoTo((prev) =>
                            e.target.checked ? [...prev, c.id] : prev.filter((id) => id !== c.id),
                          )
                        }
                        sx={{ color: '#5c68e0', '&.Mui-checked': { color: '#5c68e0' } }}
                      />
                      <Typography variant="body2" color={c.email ? 'text.primary' : 'text.disabled'}>
                        Also send to {c.name}
                        {!c.email && ' (no email on file)'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {partnerRecipient && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  A partner email goes to that partner only.
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <TextField label="To" value={to} onChange={(e) => setTo(e.target.value)} fullWidth required size="small" />
              <Stack direction="row" spacing={2}>
                <TextField label="CC" value={cc} onChange={(e) => setCc(e.target.value)} fullWidth size="small" placeholder="comma-separated" />
                <TextField label="BCC" value={bcc} onChange={(e) => setBcc(e.target.value)} fullWidth size="small" placeholder="comma-separated" />
              </Stack>
            </>
          )}

          {templates.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel id="email-template-label">Template</InputLabel>
              <Select
                labelId="email-template-label"
                label="Template"
                value={selectedTemplate}
                onChange={(e) => { void applyTemplate(e.target.value as string); }}
                disabled={loadingTemplate}
              >
                <MenuItem value=""><em>None (write your own)</em></MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} fullWidth required size="small" />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Message</Typography>
            {loadingTemplate ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
            ) : (
              <EmailHtmlEditor html={html} onChange={setHtml} minHeight={200} />
            )}
          </Box>

          <Box sx={{ bgcolor: '#f9fafb', p: 2, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Attachments</Typography>
              <MuiButton component="label" size="small" startIcon={<AttachFileIcon fontSize="small" />} sx={{ textTransform: 'none' }}>
                Attach file
                <input type="file" hidden multiple onChange={(e) => handleAddFiles(e.target.files)} />
              </MuiButton>
            </Box>
            {docs.length === 0 && files.length === 0 && (
              <Typography variant="caption" color="text.secondary">No documents available</Typography>
            )}
            {docs.map((att, idx) => (
              <Box key={`${att.label}-${idx}`} sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={att.checked}
                  size="small"
                  onChange={(e) => setDocs((prev) => prev.map((a, i) => (i === idx ? { ...a, checked: e.target.checked } : a)))}
                  sx={{ color: '#5c68e0', '&.Mui-checked': { color: '#5c68e0' } }}
                />
                <Typography variant="body2">{att.label}</Typography>
              </Box>
            ))}
            {uploadsTooLarge && (
              <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                Uploaded files total {(uploadBytes / 1024 / 1024).toFixed(1)} MB; the limit is 14 MB. Remove a file, or attach it from the case documents instead.
              </Typography>
            )}
            {files.map((f, idx) => (
              <Box key={`${f.filename}-${idx}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">📎 {f.filename}</Typography>
                <IconButton size="small" onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose} disabled={sending}>Cancel</MuiButton>
        <MuiButton onClick={handleSend} variant="contained" color="primary" disabled={sending || !canSend}>
          {sending ? <CircularProgress size={20} /> : 'Send'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
}
